import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

interface NavigationContextType {
  isNavigating: boolean;
  setIsNavigating: (isNavigating: boolean) => void;
  navigationStarted: (destination?: string) => void;
  navigationCompleted: () => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isNavigating, setIsNavigating] = useState(false);

  /*
   * Both timeouts below are tracked so they can be cancelled.
   *
   * They were fire-and-forget, which meant a provider unmounted inside 1.5s of
   * a navigation still had a timer holding a reference to its setState -- it
   * fired afterwards and updated a component that was gone. In the app that is
   * a harmless no-op React warns about; in the test run it surfaced as an
   * unhandled error attributed to whichever spec happened to be tearing down at
   * the time, which is a confusing place to go looking for it.
   *
   * One ref, not two, because only the most recent timeout matters: each of
   * these sets the same flag to the same value, so an earlier pending one has
   * nothing left to contribute and replacing it is the intended behaviour.
   */
  const clearPending = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const scheduleClear = (afterMs: number) => {
    if (clearPending.current) clearTimeout(clearPending.current);
    clearPending.current = setTimeout(() => {
      clearPending.current = undefined;
      setIsNavigating(false);
    }, afterMs);
  };

  const navigationStarted = (destination?: string) => {
    // COMPLETELY SKIP loading state for article navigation
    if (destination && destination.startsWith('/blog/article/')) {
      return; // Do nothing - no loading state for articles
    }

    setIsNavigating(true);

    // Safety timeout to prevent stuck loading state
    scheduleClear(1500); // 1.5 second max loading time
  };

  const navigationCompleted = () => {
    // Small delay to ensure smooth transition completion
    scheduleClear(50);
  };

  // Auto-clear navigation state on mount, and drop any pending timer on unmount
  // so it cannot set state on a provider that is no longer mounted.
  useEffect(() => {
    setIsNavigating(false);
    return () => {
      if (clearPending.current) clearTimeout(clearPending.current);
    };
  }, []);

  return (
    <NavigationContext.Provider 
      value={{ 
        isNavigating, 
        setIsNavigating, 
        navigationStarted, 
        navigationCompleted 
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
};